/**
 * やること(■17 第1弾・2026-08-31 発注者確定)。
 *
 * 第1弾は「担当画面へのショートカット一覧」。ログインユーザーの権限に応じて、
 * 承認・支払い・受領・支払い済み申請確認・完了確認の**未処理件数**を出し、
 * タップでその画面へ移動する。
 * 第2弾(運用開始後)で、これらを1画面に統合した処理インボックスにする予定。
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import { useToast } from '@core/components/Toast';
import { subscribeMyUnread } from './notificationsRepo';
import {
  fetchForApproval,
  fetchForCashHandover,
  fetchForCompletion,
  fetchForManagerCheck,
  fetchRecurringForManagerCheck,
  fetchForPaymentByAccounting,
  fetchForPaymentByApprover,
  fetchForPaymentByBucho,
  fetchForReceive,
  fetchForReimbursement,
  fetchForReportAccountingConfirm,
  fetchForSelfPayment,
} from './flowActions';

/** 担当事業部が無いときに毎回新しい配列を作らない(useCallback の依存が揺れて再取得が続くのを防ぐ) */
const NO_DEPTS: import('@core/lib/types').DepartmentId[] = [];

type Item = { key: string; label: string; to: string; count: number };

/** ページを行き来したとき前回の件数を即表示するためのキャッシュ(2026-08-31) */
const cache = new Map<string, Item[]>();

export default function TodoPage() {
  const { user, flowRole } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const cached = user ? cache.get(user.id) : undefined;
  const [items, setItems] = useState<Item[]>(cached ?? []);
  const [loading, setLoading] = useState(!cached);

  const isBucho = flowRole === 'bucho';
  const isApprover = flowRole === 'approver';
  const isAccounting = flowRole === 'accounting';
  /** 担当事業部を持つ承認者は部門長としても動く(2026-09-08 指示) */
  const managerDepts = user?.manager_department_ids ?? NO_DEPTS;
  const actsAsBucho = isBucho || (isApprover && managerDepts.length > 0);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const [manager, approval, selfPay, payAcc, payBucho, payApprover, cashHand, reimburse, receive, complete, acct, recurringCheck] =
        await Promise.all([
          actsAsBucho ? fetchForManagerCheck(managerDepts) : Promise.resolve([]),
          isApprover ? fetchForApproval() : Promise.resolve([]),
          fetchForSelfPayment(user.id),
          isAccounting ? fetchForPaymentByAccounting() : Promise.resolve([]),
          actsAsBucho ? fetchForPaymentByBucho(managerDepts) : Promise.resolve([]),
          isApprover ? fetchForPaymentByApprover(user.id) : Promise.resolve([]),
          isAccounting ? fetchForCashHandover() : Promise.resolve([]),
          isAccounting ? fetchForReimbursement() : Promise.resolve([]),
          fetchForReceive(user.id, user.manager_department_ids, isApprover || isAccounting),
          isApprover ? fetchForCompletion() : Promise.resolve([]),
          isAccounting ? fetchForReportAccountingConfirm() : Promise.resolve([]),
          // 繰り返し支払いの部門長確認(2026-10-15)
          actsAsBucho ? fetchRecurringForManagerCheck(user.id, managerDepts) : Promise.resolve([]),
        ]);

      const next: Item[] = [];
      const approveCount = manager.length + recurringCheck.length + approval.length;
      if (isBucho || isApprover) {
        next.push({
          key: 'approve',
          label: '承認',
          to: '/approve',
          count: approveCount,
        });
      }
      next.push({
        key: 'pay',
        label: '支払い待ち',
        to: '/pay',
        // 承認者の代行分と担当事業部の部門長分は同じ申請が重なることがあるので、IDで数える
        count: new Set([...selfPay, ...payAcc, ...payBucho, ...payApprover, ...cashHand, ...reimburse].map((t) => t.id)).size,
      });
      next.push({ key: 'receive', label: '受領待ち', to: '/receive-wait', count: receive.length });
      if (isAccounting) {
        next.push({
          key: 'acct',
          label: '支払い済み申請確認',
          to: '/acct-confirm',
          count: acct.length,
        });
      }
      if (isApprover) {
        next.push({ key: 'confirm', label: '完了確認', to: '/confirm', count: complete.length });
      }
      setItems(next);
      cache.set(user.id, next);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'やることを取得できませんでした');
    } finally {
      setLoading(false);
    }
  }, [user, isBucho, isApprover, isAccounting, actsAsBucho, managerDepts, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  // 通知が届いたら件数も更新する(他ページと同じ仕様)
  useEffect(() => {
    if (!user) return;
    let first = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = subscribeMyUnread(user.id, () => {
      if (first) {
        first = false;
        return;
      }
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void load(), 800);
    });
    const onVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
      if (timer) clearTimeout(timer);
    };
  }, [user, load]);

  const total = items.reduce((s, i) => s + i.count, 0);

  return (
    <section className="screen" style={{ maxWidth: 900 }}>
      <div className="card">
        <h3>
          やること
          {!loading && (
            <span className="hint" style={{ fontWeight: 400 }}>
              {total > 0 ? `(未処理 ${total}件)` : '(未処理はありません)'}
            </span>
          )}
        </h3>

        {loading ? (
          <p className="empty">読み込み中…</p>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {items.map((i) => (
              <button
                key={i.key}
                className="todo-row"
                onClick={() => navigate(i.to)}
                aria-label={`${i.label} ${i.count}件`}
              >
                {/* 説明文は表示しない(2026-08-31 指示)。項目名と件数だけ */}
                <span style={{ minWidth: 0 }}>
                  <b style={{ fontSize: 14 }}>{i.label}</b>
                </span>
                <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                  {i.count > 0 ? (
                    <span className="badge b-pend" style={{ fontWeight: 700 }}>
                      {i.count}件
                    </span>
                  ) : (
                    <span className="hint">なし</span>
                  )}
                  <span style={{ color: 'var(--muted)' }}>›</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
